import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  MapPin, 
  ArrowLeft,
  FileText,
  Calendar,
  RefreshCw,
  Loader2,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE_URL } from '@/lib/apiBase';

const API = API_BASE_URL;

const LocationDetailPage = () => {
  const { locationId } = useParams();
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({ fields: false, calendars: false });

  const fetchLocation = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/locations/${locationId}`);
      setLocation(response.data);
    } catch (error) {
      console.error('Failed to fetch location:', error);
      toast.error('Failed to load location');
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    fetchLocation();
  }, [fetchLocation]);

  const handleSync = async (type) => {
    setSyncing(prev => ({ ...prev, [type]: true }));
    try {
      const endpoint = type === 'fields' ? 'sync-fields' : 'sync-calendars';
      const response = await axios.post(`${API}/locations/${locationId}/${endpoint}`);
      toast.success(response.data.message);
      fetchLocation();
    } catch (error) {
      toast.error(`Failed to sync ${type}`);
    } finally {
      setSyncing(prev => ({ ...prev, [type]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  if (!location) {
    return (
      <div className="text-center py-12">
        <MapPin className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <p className="text-slate-500">Location not found</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/locations">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Locations
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="location-detail-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/locations" className="hover:text-slate-700">Locations</Link>
        <span>/</span>
        <span className="text-slate-900 font-medium">{location.name}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
            <MapPin className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {location.name}
            </h1>
            <p className="text-slate-500">{location.location_id}</p>
          </div>
        </div>
        <span className={`badge-${location.connected ? 'success' : 'error'}`}>
          {location.connected ? 'Connected' : 'Disconnected'}
        </span>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="card-hover">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <FileText className="w-5 h-5 text-indigo-600" />
                Custom Fields
              </CardTitle>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleSync('fields')}
                disabled={syncing.fields}
              >
                {syncing.fields ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-slate-500 mb-4">
              Manage field visibility and sync custom fields from CRM.
            </p>
            <Button asChild className="w-full bg-indigo-600 hover:bg-indigo-700">
              <Link to={`/locations/${locationId}/fields`}>
                View Fields
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Calendar className="w-5 h-5 text-amber-600" />
                Calendars
              </CardTitle>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleSync('calendars')}
                disabled={syncing.calendars}
              >
                {syncing.calendars ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-slate-500 mb-4">
              Configure working hours, slot duration, and availability.
            </p>
            <Button asChild className="w-full bg-indigo-600 hover:bg-indigo-700">
              <Link to={`/locations/${locationId}/calendars`}>
                View Calendars
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Users className="w-5 h-5 text-indigo-600" />
                CRM Users
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-slate-500 mb-4">
              View CRM users synced for this location.
            </p>
            <Button asChild className="w-full bg-indigo-600 hover:bg-indigo-700">
              <Link to={`/locations/${locationId}/users`}>
                View Users
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LocationDetailPage;
